import { useMemo, useState } from "react";
import {
  ArrowUp,
  Calendar,
  ChevronRight,
  FolderOpen,
  LayoutGrid,
  List,
  Search,
  User,
} from "lucide-react";
import { Input } from "@hub/ui";
import type { ViewPrimitive, BladeContext } from "../../shell/registry";
import {
  BladeLoading,
  BladeError,
  BladeDenied,
  BladeEmpty,
  forcedBladeState,
} from "../../shell/BladeStates";
import { useAuditBatches, useAuditQueues } from "../AuditDataSource";
import { useTourNavigator } from "../help/TourProvider";
import { useIsTourDemoData } from "../help/TourDemoData";
import { EvalScopeBanner } from "../EvalScopeBanner";
import {
  BATCH_COLUMNS,
  BATCH_GROUP_FIELDS,
  groupBatches,
  sortBatches,
  type BatchColumn,
  type BatchField,
  type BatchGroupContext,
  type BatchGroupField,
  type SortDirection,
} from "./batchGrouping";
import {
  BATCH_PAGE_SIZE,
  hasCodingShortfall,
  hasFACounts,
  pageCount,
  pageOf,
  reviewableBatches,
  applyBatchFilters,
  hasActiveFilters,
  dayBoundaryMs,
  DEFAULT_BATCH_FILTERS,
  type BatchFilters,
  type CodingFilter,
  type StatusFilter,
  type AssignmentFilter,
} from "./batchFilter";
import type { AuditBatch } from "../types";
import {
  indexAssignments,
  assignedBatchIds,
  assignmentLabel,
  type BatchAssignment,
} from "./queueAssignment";
import {
  BatchNumber,
  BatchStateBadge,
  BatchFlags,
  CoderStack,
  coderStackLabel,
  JobCodeChip,
  Progress,
  batchActionLabel,
} from "./parts";

// Batch-queue view-primitive (HUB-504, slice 1). The auditor's landing screen:
// every FA batch as a card or a table row, with a card/table toggle and a
// search across batch/client/day/case/patient/MRN. A faithful port of the prototype's AuditQueue batch-grid + table branches,
// re-skinned onto @hub/ui + the design system. Blind-safe: batches carry only
// counts + case summaries, never A/B results or provenance.

const VIEW_STORAGE_KEY = "cw_batch_view";
type BatchView = "card" | "table";

function readStoredView(): BatchView {
  try {
    return localStorage.getItem(VIEW_STORAGE_KEY) === "table"
      ? "table"
      : "card";
  } catch {
    return "card";
  }
}

function storeView(v: BatchView): void {
  try {
    localStorage.setItem(VIEW_STORAGE_KEY, v);
  } catch {
    /* localStorage unavailable (private mode / tests) — non-fatal */
  }
}

/** Matches the batch number + client/day/job at batch level, plus
 *  case id/patient/MRN/payer within the batch. */
function batchMatches(batch: AuditBatch, needle: string): boolean {
  if (batch.batchNumber.toLowerCase().includes(needle)) return true;
  if (batch.client.toLowerCase().includes(needle)) return true;
  if (batch.dos.toLowerCase().includes(needle)) return true;
  if (batch.jobCode.toLowerCase().includes(needle)) return true;
  return batch.cases.some(
    (c) =>
      c.id.toLowerCase().includes(needle) ||
      // The EPISODE ID as well as the DCN. The case tables label a case by its
      // episode id, so that is the number a reviewer copies off the screen and
      // types in here — matching only the DCN would return nothing for the one
      // identifier the list actually showed them.
      c.episodeId.toLowerCase().includes(needle) ||
      c.patientFull.toLowerCase().includes(needle) ||
      c.mrn.toLowerCase().includes(needle) ||
      c.payer.toLowerCase().includes(needle),
  );
}

/** Open the batch detail (case list) DIRECTLY in focus mode. Focus-mode-first
 *  (HUB-504 slice 2): a single click on a batch card drills into the batch detail
 *  full-bleed — no explorer intermediate, no breadcrumb-click needed. `openChild`
 *  pushes the detail onto the focus drill-stack; `setMode('focus')` guarantees the
 *  full-bleed presentation even if the canvas had been toggled to explorer.
 *
 *  Stops propagation so the blade's own click-to-activate doesn't fire afterwards and
 *  steal the active index back to this (queue) blade — which in focus mode would
 *  re-show the queue instead of the freshly-opened batch detail. */
function openBatch(
  ctx: BladeContext,
  batch: AuditBatch,
  e?: { stopPropagation: () => void },
): void {
  e?.stopPropagation();
  ctx.openChild({
    key: "audit-batch",
    entityId: batch.id,
    eyebrow: batch.jobCode,
    title: batch.batchNumber,
    width: "xwide",
  });
  ctx.setMode("focus");
}

function discrepancyTextClass(count: number): string {
  return count > 0 ? "text-red-600" : "text-coronis-teal-600";
}

/**
 * FA's coded / uncoded / inactive split for a batch.
 *
 * Shown only when it says something. A fully-coded batch's split is "all of
 * them", which the state pill already carries, and a count identical on every
 * row teaches a reviewer to stop reading the column — the same fault the
 * constant FA flags were removed for.
 *
 * These are FA's counts for the WHOLE batch, which is not the same number as
 * the cases this reviewer can see. Labelled "FA" so the two are not read as one
 * disagreeing figure.
 */
function CodingShortfall({
  batch,
  compact,
  className,
}: {
  batch: AuditBatch;
  compact?: boolean;
  className?: string;
}) {
  // A batch with no FA record at all is an ingest-day bucket. Rendering
  // "0 active · 0 coded · 0 inactive" for it would read as a batch where nothing
  // has been done, rather than one FA has never heard of.
  if (!hasFACounts(batch)) {
    return compact ? (
      <span className="text-[11px] text-muted-foreground">—</span>
    ) : (
      <span
        className={`block text-[11px] text-muted-foreground ${className ?? ""}`}
      >
        no FA batch record
      </span>
    );
  }

  // Every batch shows the same three figures, including the fully-coded ones.
  // This used to render nothing without a shortfall, on the reasoning that "all
  // of them" is already implied by the state pill. In practice the reviewer
  // cannot tell a 4-episode batch from a 400-episode one without opening it, and
  // a column that disappears on some rows cannot be scanned down.
  //
  // Zeros are printed rather than omitted for the same reason: the figures line
  // up across cards only if they are always in the same place.
  const total = batch.activeEpisodes + batch.inactiveEpisodes;
  const text =
    `${total} episode${total === 1 ? "" : "s"} · ` +
    `${batch.codedEpisodes} coded · ` +
    `${batch.inactiveEpisodes} inactive`;

  return (
    <span
      className={`block text-[11px] ${
        hasCodingShortfall(batch) ? "text-amber-700" : "text-muted-foreground"
      } ${className ?? ""}`}
      title={
        `FirstAnesthesia reports ${batch.activeEpisodes} active episode(s) in this batch, ` +
        `${batch.codedEpisodes} of them coded, and ${batch.inactiveEpisodes} withdrawn. ` +
        `Total is active + inactive. These are FA's counts for the whole batch, ` +
        `which need not equal the cases you can see.`
      }
    >
      {compact ? text : `FA: ${text}`}
    </span>
  );
}

/**
 * Who a batch is assigned to.
 *
 * Renders NOTHING when the queues have not loaded — "Unassigned" would be a
 * claim, and a failed queue read and genuinely unassigned work are different
 * facts. `queuesLoaded` is what separates them.
 */
function AssignedTo({
  assignment,
  queuesLoaded,
  compact,
}: {
  assignment: BatchAssignment | undefined;
  queuesLoaded: boolean;
  compact?: boolean;
}) {
  if (!queuesLoaded) return null;
  const label = assignmentLabel(assignment);
  if (!label) {
    return (
      <span className="text-[11px] text-muted-foreground">
        {compact ? "—" : "Unassigned"}
      </span>
    );
  }
  return (
    <span
      className="inline-flex items-center gap-1 text-[11px] text-foreground"
      title={
        assignment?.shared
          ? `Visible to ${assignment.reviewerIds.length} reviewers: ${assignment.reviewerLabels.join(", ")}`
          : `Assigned to ${label}`
      }
    >
      <User className="size-3 text-muted-foreground" aria-hidden />
      {label}
      {assignment?.shared ? (
        <span className="text-muted-foreground">· shared</span>
      ) : null}
    </span>
  );
}

function BatchCard({
  batch,
  ctx,
  assignment,
  queuesLoaded,
}: {
  batch: AuditBatch;
  ctx: BladeContext;
  assignment: BatchAssignment | undefined;
  queuesLoaded: boolean;
}) {
  return (
    <button
      type="button"
      onClick={(e) => openBatch(ctx, batch, e)}
      className="group flex flex-col overflow-hidden rounded-coronis border border-border bg-card text-left transition-shadow hover:border-coronis-blue-200 hover:shadow-md"
    >
      <div className="flex flex-col gap-2 px-4 pb-2.5 pt-3.5">
        {/* Batch code, then the client ONCE. The card used to lead with a
            server-composed label that already embedded the client ("ACME ·
            B-118"), then repeat the client as its own line, then again as a
            job chip — the same name three times on one card, in a list where
            most rows share it. The chip stays because it is the account code a
            reviewer searches by, and it is not the client's name.

            The facility line is gone entirely: facility is per EPISODE and a
            batch may span several, so the batch-level value was always empty
            and rendered as a lone separator. */}
        <div className="flex min-w-0 flex-col gap-1">
          <BatchNumber
            value={batch.batchNumber}
            className="truncate text-[16px] font-bold leading-tight"
          />
          <span className="flex min-w-0 items-center gap-2">
            <span className="truncate text-[13px] font-semibold text-foreground">
              {batch.client}
            </span>
            <JobCodeChip code={batch.jobCode} />
          </span>
          {batch.dos && (
            <span className="flex items-center gap-1.5 text-[11.5px] text-muted-foreground">
              <Calendar className="size-3 shrink-0" /> {batch.dos}
            </span>
          )}
        </div>
        {/* Pills on their OWN row, not competing with the identity block.
            Side by side they took 177px of a 316px card, leaving the client
            name 46px of the 213px it needs — "Anesthesia Assoc of Greater
            Miami" rendered as "Anest…". Labelling the FA status is what tipped
            it: the raw "FA C" fitted where "FA Closed" does not. The client is
            what a reviewer scans for, so it gets the width. */}
        <span className="flex flex-wrap items-center gap-1.5">
          <BatchStateBadge state={batch.state} />
          <BatchFlags faStatus={batch.faStatus} />
        </span>
      </div>

      <div className="flex items-baseline gap-5 px-4 pb-3">
        <div className="flex flex-col">
          <span className="text-2xl font-semibold leading-none text-foreground">
            {batch.caseCount}
          </span>
          <span className="mt-0.5 text-[11px] text-muted-foreground">
            {batch.caseCount === 1 ? "case" : "cases"}
          </span>
        </div>
        {/* Cases carrying BOTH sides — a human coder and the auto-coder.
            Comparability is what makes a discrepancy count mean anything, and
            nothing in the UI said how many there were: a 40-case batch showing
            0 discrepancies read as perfect agreement whether both sides had
            been coded or neither had.

            Safe to show, and deliberately so. The server counts it from
            agent/coder rather than the A/B slots, and the comparison is
            symmetric, so the figure is identical under either arrangement — a
            number that moved with the arrangement would leak it. */}
        <div className="flex flex-col">
          <span className="text-2xl font-semibold leading-none text-foreground">
            {batch.comparableCaseCount}
          </span>
          <span className="mt-0.5 text-[11px] text-muted-foreground">
            both sides coded
          </span>
        </div>
        <div className="flex flex-col">
          <span
            className={`text-2xl font-semibold leading-none ${discrepancyTextClass(batch.discrepancyCaseCount)}`}
          >
            {batch.discrepancyCaseCount}
          </span>
          <span className="mt-0.5 text-[11px] text-muted-foreground">
            with discrepancies
          </span>
        </div>
      </div>

      <div className="px-4 pb-2">
        <AssignedTo assignment={assignment} queuesLoaded={queuesLoaded} />
      </div>

      <CodingShortfall batch={batch} className="px-4 pb-3" />

      <div className="px-4 pb-3">
        <div className="mb-1.5 flex justify-between text-[11px] text-muted-foreground">
          <span>
            {batch.reviewedCount} / {batch.caseCount} reviewed
          </span>
          {batch.remainingCount > 0 && <span>{batch.remainingCount} left</span>}
        </div>
        <Progress
          value={batch.reviewedCount}
          max={batch.caseCount}
          tone={batch.state === "complete" ? "teal" : "amber"}
        />
      </div>

      <div className="flex items-center justify-between border-t border-border bg-muted/40 px-4 py-2.5">
        <span className="flex items-center gap-2">
          <CoderStack coderIds={batch.coders} />
          <span className="text-[11.5px] text-muted-foreground">
            {coderStackLabel(batch.coders, batch.coderNames)}
          </span>
        </span>
        <span className="flex items-center gap-1.5 text-[12.5px] font-semibold text-coronis-blue-600">
          {batchActionLabel(batch.state)} <ChevronRight className="size-3.5" />
        </span>
      </div>
    </button>
  );
}

/** A sortable column header. Renders the direction only on the ACTIVE column —
 *  an arrow on every header says nothing about which one is in force. */
function BatchHeader({
  column,
  sortField,
  sortDir,
  onToggle,
}: {
  column: BatchColumn;
  sortField: BatchField;
  sortDir: SortDirection;
  onToggle: (field: BatchField) => void;
}) {
  const active = column.field === sortField;
  const className = `px-3 py-2 font-medium ${column.align === "center" ? "text-center" : ""} ${column.className ?? ""}`;
  if (!column.sortable) {
    return (
      <th className={className} title={column.title}>
        {column.label}
      </th>
    );
  }
  return (
    <th
      className={className}
      // aria-sort is what tells a screen reader which column orders the table
      // and which way; the arrow glyph below is invisible to one.
      aria-sort={
        active ? (sortDir === "asc" ? "ascending" : "descending") : "none"
      }
      title={column.title}
    >
      <button
        type="button"
        onClick={() => onToggle(column.field)}
        className={`flex items-center gap-1 whitespace-nowrap font-medium uppercase tracking-[0.04em] transition-colors hover:text-foreground ${
          active ? "text-foreground" : ""
        } ${column.align === "center" ? "mx-auto" : ""}`}
      >
        {column.label}
        <ArrowUp
          className={`size-3 shrink-0 transition-transform ${sortDir === "desc" ? "rotate-180" : ""} ${
            // A dimmed placeholder on every sortable header, so the columns do
            // not shift sideways as the active one changes.
            active ? "" : "opacity-0"
          }`}
          aria-hidden
        />
      </button>
    </th>
  );
}

function BatchTable({
  assignments,
  queuesLoaded,
  batches,
  ctx,
  sortField,
  sortDir,
  onToggleSort,
}: {
  batches: AuditBatch[];
  ctx: BladeContext;
  assignments: ReadonlyMap<string, BatchAssignment>;
  queuesLoaded: boolean;
  sortField: BatchField;
  sortDir: SortDirection;
  onToggleSort: (field: BatchField) => void;
}) {
  return (
    <div className="overflow-auto rounded-coronis border border-border">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-border bg-muted/40 text-left text-[11px] font-medium uppercase tracking-[0.04em] text-muted-foreground">
            {/* Every column, from BATCH_COLUMNS — the same list the sort
                dropdown reads. The two used to be written out separately, which
                is how the dropdown came to offer two fields over a
                fourteen-column table. */}
            {BATCH_COLUMNS.map((col) => (
              <BatchHeader
                key={col.field}
                column={col}
                sortField={sortField}
                sortDir={sortDir}
                onToggle={onToggleSort}
              />
            ))}
            <th className="w-[70px] px-3 py-2" />
          </tr>
        </thead>
        <tbody>
          {batches.map((b) => (
            <tr
              key={b.id}
              onClick={(e) => openBatch(ctx, b, e)}
              className="cursor-pointer border-b border-border last:border-0 transition-colors hover:bg-muted"
            >
              {/* px-3 like every other cell, matching the generated header
                  row — the hand-written header this replaced had its own
                  padding on the first column. */}
              <td className="whitespace-nowrap px-3 py-2.5">
                <BatchNumber
                  value={b.batchNumber}
                  className="text-[13px] font-semibold"
                />
              </td>
              <td className="px-3 py-2.5 text-muted-foreground">{b.dos}</td>
              <td className="max-w-[180px] px-3 py-2.5">
                <span className="block truncate text-foreground">
                  {b.client}
                </span>
              </td>
              <td className="px-3 py-2.5">
                <JobCodeChip code={b.jobCode} />
              </td>
              <td className="px-3 py-2.5">
                <CodingShortfall batch={b} compact />
              </td>
              <td className="whitespace-nowrap px-3 py-2.5 text-muted-foreground">
                {b.closeDate || "—"}
              </td>
              <td className="whitespace-nowrap px-3 py-2.5 text-muted-foreground">
                {b.qcDueDate || "—"}
              </td>
              <td className="px-3 py-2.5 text-center font-semibold text-foreground">
                {b.caseCount}
              </td>
              <td className="px-3 py-2.5 text-center tabular-nums text-foreground">
                {b.comparableCaseCount}
              </td>
              <td
                className={`px-3 py-2.5 text-center font-semibold ${discrepancyTextClass(b.discrepancyCaseCount)}`}
              >
                {b.discrepancyCaseCount}
              </td>
              <td className="px-3 py-2.5">
                <div className="flex items-center gap-2">
                  <div className="min-w-[70px] flex-1">
                    <Progress
                      value={b.reviewedCount}
                      max={b.caseCount}
                      tone={b.state === "complete" ? "teal" : "amber"}
                    />
                  </div>
                  <span className="shrink-0 text-[10.5px] text-muted-foreground">
                    {b.reviewedCount}/{b.caseCount}
                  </span>
                </div>
              </td>
              <td className="px-3 py-2.5">
                <AssignedTo
                  assignment={assignments.get(b.id)}
                  queuesLoaded={queuesLoaded}
                  compact
                />
              </td>
              <td className="px-3 py-2.5">
                <CoderStack coderIds={b.coders} />
              </td>
              <td className="px-3 py-2.5">
                <span className="flex flex-wrap items-center gap-1.5">
                  <BatchStateBadge state={b.state} />
                  <BatchFlags faStatus={b.faStatus} />
                </span>
              </td>
              <td className="px-3 py-2.5">
                <span className="flex items-center gap-1 text-xs font-semibold text-coronis-blue-600">
                  {batchActionLabel(b.state)}{" "}
                  <ChevronRight className="size-3" />
                </span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/**
 * Page controls for the batch list.
 *
 * FA's own coding batch list pages, and for the same reason: past a few hundred
 * rows the list stops being scannable. Client-side over the already-fetched
 * list — the read model now serves the whole set from memory, so the cost
 * paging would have saved is no longer there, and search and sort stay instant
 * across every page rather than only the current one.
 *
 * The count says how many of how many, because a pager that shows only "page 2
 * of 14" leaves a reviewer unable to tell a filtered list from a long one.
 */
function Pager({
  page,
  totalPages,
  total,
  shown,
  onChange,
}: {
  page: number;
  totalPages: number;
  total: number;
  shown: number;
  onChange: (p: number) => void;
}) {
  const from = page * BATCH_PAGE_SIZE + 1;
  return (
    <div className="flex flex-wrap items-center justify-between gap-3 border-t border-border pt-3">
      <span className="text-xs text-muted-foreground">
        {from}–{from + shown - 1} of {total} batches
      </span>
      <div className="flex items-center gap-2">
        <button
          type="button"
          className="rounded-coronis border border-border px-2.5 py-1 text-xs font-medium text-foreground disabled:opacity-40"
          onClick={() => onChange(page - 1)}
          disabled={page <= 0}
        >
          Previous
        </button>
        <span className="text-xs tabular-nums text-muted-foreground">
          Page {page + 1} of {totalPages}
        </span>
        <button
          type="button"
          className="rounded-coronis border border-border px-2.5 py-1 text-xs font-medium text-foreground disabled:opacity-40"
          onClick={() => onChange(page + 1)}
          disabled={page >= totalPages - 1}
        >
          Next
        </button>
      </div>
    </div>
  );
}

/**
 * Says plainly when the walkthrough is running on example data.
 *
 * Not optional chrome. These batches are fictitious, and a reviewer who takes
 * them for their own queue will go looking for work that does not exist — or
 * worse, read a demo case's coding as something they are meant to judge. The
 * substitution happens precisely when their real queue is empty, so there is
 * nothing else on screen to contradict it.
 */
function TourDemoBanner() {
  if (!useIsTourDemoData()) return null;
  return (
    <div
      className="flex items-center gap-2 rounded-coronis border px-3 py-2 text-[12px]"
      style={{
        borderColor: "color-mix(in srgb, var(--ch-amber-500) 40%, transparent)",
        background:
          "color-mix(in srgb, var(--ch-amber-500) 10%, var(--bg-surface))",
      }}
      role="status"
    >
      <span className="font-semibold text-coronis-amber-700">Example data</span>
      <span className="text-muted-foreground">
        Your queue is empty, so the walkthrough is using sample batches. None of
        these are real cases.
      </span>
    </div>
  );
}

/** Stable empty set for when the queues have not loaded, so the memo's
 *  dependency does not change identity on every render. */
const NO_ASSIGNMENTS: ReadonlySet<string> = new Set<string>();
const NO_INDEX: ReadonlyMap<string, BatchAssignment> = new Map();

function AuditBatchesBody({ ctx }: { ctx: BladeContext }) {
  const { status, data } = useAuditBatches();
  const [query, setQuery] = useState("");
  const [view, setView] = useState<BatchView>(readStoredView);
  // Newest first, ungrouped: the auditor's stated default is "all batches
  // reverse sorted by date".
  const [sortField, setSortField] = useState<BatchField>("date");
  const [sortDir, setSortDir] = useState<SortDirection>("desc");
  const [groupField, setGroupField] = useState<BatchGroupField | "none">(
    "none",
  );
  // Default to batches worth reviewing: FA's coding complete, or the batch
  // closed. The unfiltered list is mostly batches still being coded, which have
  // no human baseline to compare against yet.
  const [onlyReviewable, setOnlyReviewable] = useState(true);
  // Date range / coding / FA status. Separate axes rather than one enum: a
  // reviewer asking for "closed but not fully coded" is asking a real question,
  // and collapsing them into a single list of states cannot express it.
  const [filters, setFilters] = useState<BatchFilters>(DEFAULT_BATCH_FILTERS);
  // The raw input text, kept beside the parsed bounds so a half-typed date does
  // not clear the control while the reviewer is still typing it.
  const [fromText, setFromText] = useState("");
  const [toText, setToText] = useState("");
  const [page, setPage] = useState(0);

  // Any filter change can leave the reviewer past the end of a shorter list.
  const updateFilters = (patch: Partial<BatchFilters>) => {
    setFilters((f) => ({ ...f, ...patch }));
    setPage(0);
  };

  // Queues load independently of batches. A failure here degrades the
  // assignment column to absent rather than emptying the batch list — the two
  // are different facts and only one of them should stop the screen working.
  const queuesQuery = useAuditQueues();
  const queues =
    queuesQuery?.status === "success" ? queuesQuery.data : undefined;
  const queuesLoaded = queues !== undefined;
  const assigned = useMemo(
    () => (queues ? assignedBatchIds(queues) : NO_ASSIGNMENTS),
    [queues],
  );
  const assignments = useMemo(
    () => (queues ? indexAssignments(queues) : NO_INDEX),
    [queues],
  );

  // What the assignee sort and the assignee grouping need. Assignments live in
  // the reviewer QUEUES, which load independently of the batches — so they are
  // passed in rather than folded onto AuditBatch.
  const groupCtx = useMemo<BatchGroupContext>(
    () => ({ assignments }),
    [assignments],
  );

  const batches = useMemo(() => data ?? [], [data]);
  const shown = useMemo(() => {
    // Reviewable first, then the search. Filtering after the search would let a
    // query surface a batch the default view deliberately excludes, so the same
    // text would find a batch that is not in the list.
    const scoped = onlyReviewable ? reviewableBatches(batches) : batches;
    // Filters before the search, for the same reason reviewability is: a query
    // must never surface a batch the active filters exclude.
    const filtered = applyBatchFilters(scoped, filters, assigned);
    const q = query.trim().toLowerCase();
    const matched = q ? filtered.filter((b) => batchMatches(b, q)) : filtered;
    return sortBatches(matched, sortField, sortDir, groupCtx);
  }, [
    batches,
    onlyReviewable,
    filters,
    assigned,
    query,
    sortField,
    sortDir,
    groupCtx,
  ]);

  // Grouped from the already-sorted list, so batches keep their order inside a
  // group rather than reverting to arrival order.
  const groups = useMemo(
    () =>
      groupField === "none" ? null : groupBatches(shown, groupField, groupCtx),
    [shown, groupField, groupCtx],
  );

  // Clicking a header sorts by it; clicking the active one reverses.
  //
  // First click on a NEW column always starts ascending rather than inheriting
  // the previous column's direction, which would silently show a descending
  // list under a header the reviewer just picked. Paging resets: a sort change
  // can leave them past the end of the list they were on.
  const toggleSort = (field: BatchField) => {
    setPage(0);
    if (field === sortField) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
      return;
    }
    setSortField(field);
    setSortDir("asc");
  };

  // The walkthrough's queue steps land here, and its "inside a batch" step needs
  // a batch open. Opening the FIRST listed one rather than a named fixture, so
  // the tour works against whatever this reviewer actually has.
  useTourNavigator((screen) => {
    if (screen === "queue") return true;
    if (screen === "batch" && shown.length > 0) {
      openBatch(ctx, shown[0]);
      return true;
    }
    return false;
  });

  const totalPages = pageCount(shown.length, BATCH_PAGE_SIZE);
  // A filter or sort change can leave the reviewer past the end of a shorter
  // list, which renders as an empty page over a non-empty result.
  const currentPage = Math.min(page, totalPages - 1);
  const paged = useMemo(
    () => pageOf(shown, currentPage, BATCH_PAGE_SIZE),
    [shown, currentPage],
  );
  const paging = groups === null && shown.length > BATCH_PAGE_SIZE;
  const listed = paging ? paged : shown;

  if (status === "loading") return <BladeLoading />;
  if (status === "error")
    return (
      <BladeError message="Something went wrong loading the evaluation queue." />
    );
  if (status === "denied")
    return (
      <BladeDenied description="You don’t have access to the code-evaluation queue." />
    );
  if (batches.length === 0) {
    return (
      <BladeEmpty
        title="No batches"
        description="There are no code-evaluation batches awaiting review."
      />
    );
  }

  const openBatches = batches.filter((b) => b.state !== "complete").length;
  const casesLeft = batches.reduce((n, b) => n + b.remainingCount, 0);

  const setViewPersisted = (v: BatchView) => {
    setView(v);
    storeView(v);
  };

  return (
    <div className="flex flex-col gap-4">
      {/* What this list MEANS for whoever is looking at it: the whole captured
          corpus, or their assignment. The rows are identical either way. */}
      <TourDemoBanner />
      <EvalScopeBanner batchCount={batches.length} />
      <div>
        <p className="text-sm text-muted-foreground">
          {openBatches} {openBatches === 1 ? "batch" : "batches"} open ·{" "}
          {casesLeft} {casesLeft === 1 ? "case" : "cases"} awaiting review ·
          showing batches whose coding is finished or closed
        </p>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3">
        <div data-tour="tour-search" className="relative w-full max-w-[320px]">
          <Search className="pointer-events-none absolute left-3 top-1/2 size-3.5 -translate-y-1/2 text-muted-foreground" />
          <Input
            className="h-9 pl-9"
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setPage(0);
            }}
            placeholder="Search batch #, client, day, case, patient, MRN…"
            aria-label="Search batches"
          />
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {/* Off shows every batch, including those FA is still coding. Those
              have no human baseline yet, so most of them cannot be reviewed —
              but a reviewer looking for a specific batch needs to be able to
              find one. */}
          <label className="flex items-center gap-1.5 text-xs text-muted-foreground">
            <input
              type="checkbox"
              className="size-3.5 accent-coronis-blue-600"
              checked={onlyReviewable}
              onChange={(e) => {
                setOnlyReviewable(e.target.checked);
                setPage(0);
              }}
            />
            Ready to review
          </label>
          {/* Date range over the batch's own date. Bounds are inclusive and
              resolved at the LOCAL day boundary — parsing YYYY-MM-DD as UTC
              would drop the "from" day's batches west of Greenwich. */}
          <label className="flex items-center gap-1.5 text-xs text-muted-foreground">
            From
            <input
              type="date"
              className="h-8 rounded-coronis border border-border bg-card px-2 text-xs text-foreground"
              value={fromText}
              onChange={(e) => {
                setFromText(e.target.value);
                updateFilters({
                  range: {
                    ...filters.range,
                    fromMs: dayBoundaryMs(e.target.value),
                  },
                });
              }}
              aria-label="Batches from date"
            />
          </label>
          <label className="flex items-center gap-1.5 text-xs text-muted-foreground">
            To
            <input
              type="date"
              className="h-8 rounded-coronis border border-border bg-card px-2 text-xs text-foreground"
              value={toText}
              onChange={(e) => {
                setToText(e.target.value);
                updateFilters({
                  range: {
                    ...filters.range,
                    toMs: dayBoundaryMs(e.target.value, true),
                  },
                });
              }}
              aria-label="Batches to date"
            />
          </label>
          <label className="flex items-center gap-1.5 text-xs text-muted-foreground">
            Coding
            <select
              className="h-8 rounded-coronis border border-border bg-card px-2 text-xs text-foreground"
              value={filters.coding}
              onChange={(e) =>
                updateFilters({ coding: e.target.value as CodingFilter })
              }
              aria-label="Filter by coding completeness"
            >
              <option value="any">Any</option>
              <option value="complete">Coded</option>
              <option value="incomplete">Not coded</option>
            </select>
          </label>
          {/* FA's own batch status. "No FA status" is its own option rather than
              folded into Open: a batch without one is an ingest-day bucket FA
              has never heard of, not a batch that is still open. */}
          <label className="flex items-center gap-1.5 text-xs text-muted-foreground">
            Status
            <select
              className="h-8 rounded-coronis border border-border bg-card px-2 text-xs text-foreground"
              value={filters.status}
              onChange={(e) =>
                updateFilters({ status: e.target.value as StatusFilter })
              }
              aria-label="Filter by FA batch status"
            >
              <option value="any">Any</option>
              <option value="open">Open</option>
              <option value="closed">Closed</option>
              <option value="unknown">No FA status</option>
            </select>
          </label>
          {/* Only offered once the queues have loaded. A filter that silently
              matches nothing because its input never arrived is worse than an
              absent control: it reads as "no batches are assigned". */}
          {queuesLoaded ? (
            <label className="flex items-center gap-1.5 text-xs text-muted-foreground">
              Assignment
              <select
                className="h-8 rounded-coronis border border-border bg-card px-2 text-xs text-foreground"
                value={filters.assignment}
                onChange={(e) =>
                  updateFilters({
                    assignment: e.target.value as AssignmentFilter,
                  })
                }
                aria-label="Filter by queue assignment"
              >
                <option value="any">Any</option>
                <option value="assigned">Assigned</option>
                <option value="unassigned">Unassigned</option>
              </select>
            </label>
          ) : null}
          {hasActiveFilters(filters) ? (
            <button
              type="button"
              className="h-8 rounded-coronis border border-border bg-card px-2 text-xs text-muted-foreground hover:text-foreground"
              onClick={() => {
                setFilters(DEFAULT_BATCH_FILTERS);
                setFromText("");
                setToText("");
                setPage(0);
              }}
            >
              Clear filters
            </button>
          ) : null}
          <label className="flex items-center gap-1.5 text-xs text-muted-foreground">
            Group
            <select
              className="h-8 rounded-coronis border border-border bg-card px-2 text-xs text-foreground"
              value={groupField}
              onChange={(e) =>
                setGroupField(e.target.value as BatchGroupField | "none")
              }
              aria-label="Group batches by"
            >
              <option value="none">None</option>
              {BATCH_GROUP_FIELDS.map((g) => (
                <option key={g.field} value={g.field}>
                  {g.label}
                </option>
              ))}
            </select>
          </label>
          {/* Both a dropdown and clickable column headers, because they answer
              different needs: the dropdown names every column in one place (and
              says which way round it is sorting), while a header click is what
              a reviewer reaches for once they can see the column they want. The
              dropdown is also the only route in CARD view, which has no
              headers, and on a narrow screen where the table scrolls sideways.

              Generated from BATCH_COLUMNS so it can never offer a different set
              of columns than the table shows. */}
          <label className="flex items-center gap-1.5 text-xs text-muted-foreground">
            Sort
            <select
              className="h-8 rounded-coronis border border-border bg-card px-2 text-xs text-foreground"
              value={`${sortField}:${sortDir}`}
              onChange={(e) => {
                const [f, d] = e.target.value.split(":");
                setSortField(f as BatchField);
                setSortDir(d as SortDirection);
                setPage(0);
              }}
              aria-label="Sort batches by"
            >
              {BATCH_COLUMNS.filter((col) => col.sortable).flatMap((col) => [
                <option key={`${col.field}:asc`} value={`${col.field}:asc`}>
                  {col.label}, ascending
                </option>,
                <option key={`${col.field}:desc`} value={`${col.field}:desc`}>
                  {col.label}, descending
                </option>,
              ])}
            </select>
          </label>
        </div>
        <div
          className="inline-flex overflow-hidden rounded-coronis border border-border"
          role="tablist"
          aria-label="Batch layout"
        >
          <button
            type="button"
            role="tab"
            aria-selected={view === "card"}
            onClick={() => setViewPersisted("card")}
            className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium transition-colors ${
              view === "card"
                ? "bg-coronis-blue-600 text-white"
                : "bg-card text-muted-foreground hover:bg-muted"
            }`}
          >
            <LayoutGrid className="size-3.5" /> Cards
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={view === "table"}
            onClick={() => setViewPersisted("table")}
            className={`flex items-center gap-1.5 border-l border-border px-3 py-1.5 text-xs font-medium transition-colors ${
              view === "table"
                ? "bg-coronis-blue-600 text-white"
                : "bg-card text-muted-foreground hover:bg-muted"
            }`}
          >
            <List className="size-3.5" /> Table
          </button>
        </div>
      </div>

      {shown.length === 0 ? (
        <div className="flex flex-col items-center gap-2 py-12 text-center text-muted-foreground">
          <FolderOpen className="size-7" />
          <div className="font-medium text-foreground">No batches match</div>
        </div>
      ) : groups ? (
        <div data-tour="tour-batches" className="flex flex-col gap-6">
          {groups.map((g) => (
            <section key={g.key} className="flex flex-col gap-3">
              <header className="flex items-baseline gap-2 border-b border-border pb-1">
                <h3 className="text-sm font-semibold text-foreground">
                  {g.label}
                </h3>
                <span className="text-xs text-muted-foreground">
                  {g.batches.length}{" "}
                  {g.batches.length === 1 ? "batch" : "batches"} · {g.caseCount}{" "}
                  {g.caseCount === 1 ? "case" : "cases"}
                </span>
              </header>
              {view === "table" ? (
                <BatchTable
                  batches={g.batches}
                  ctx={ctx}
                  assignments={assignments}
                  queuesLoaded={queuesLoaded}
                  sortField={sortField}
                  sortDir={sortDir}
                  onToggleSort={toggleSort}
                />
              ) : (
                <div className="grid grid-cols-[repeat(auto-fill,minmax(300px,1fr))] gap-4">
                  {g.batches.map((b) => (
                    <BatchCard
                      key={b.id}
                      batch={b}
                      ctx={ctx}
                      assignment={assignments.get(b.id)}
                      queuesLoaded={queuesLoaded}
                    />
                  ))}
                </div>
              )}
            </section>
          ))}
        </div>
      ) : view === "table" ? (
        <div data-tour="tour-batches">
          <BatchTable
            batches={listed}
            ctx={ctx}
            assignments={assignments}
            queuesLoaded={queuesLoaded}
            sortField={sortField}
            sortDir={sortDir}
            onToggleSort={toggleSort}
          />
        </div>
      ) : (
        <div
          data-tour="tour-batches"
          className="grid grid-cols-[repeat(auto-fill,minmax(300px,1fr))] gap-4"
        >
          {listed.map((b) => (
            <BatchCard
              key={b.id}
              batch={b}
              ctx={ctx}
              assignment={assignments.get(b.id)}
              queuesLoaded={queuesLoaded}
            />
          ))}
        </div>
      )}

      {paging && (
        <Pager
          page={currentPage}
          totalPages={totalPages}
          total={shown.length}
          shown={listed.length}
          onChange={setPage}
        />
      )}
    </div>
  );
}

export const auditBatchesPrimitive: ViewPrimitive = {
  key: "audit-batches",
  // Restored when a deep link or refresh decodes this blade from the URL,
  // which carries identity only.
  defaultTitle: "Code evaluation — batches",
  defaultEyebrow: "Evaluation queue",
  defaultWidth: "xwide",
  render: (ctx) => {
    const forced = forcedBladeState(ctx.descriptor);
    if (forced === "loading") return <BladeLoading />;
    if (forced === "error") return <BladeError />;
    if (forced === "denied") return <BladeDenied />;
    if (forced === "empty") {
      return (
        <BladeEmpty
          title="No batches"
          description="There are no coding-audit batches awaiting review."
        />
      );
    }
    return <AuditBatchesBody ctx={ctx} />;
  },
};
